import React, { useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { graphql } from "relay-runtime";
import { useMutation } from "react-relay";
import { toast } from "sonner";
import { useIntl } from "react-intl";

import {
  $createTextNode,
  $insertNodes,
  DROP_COMMAND,
  LexicalEditor,
} from "lexical";
import { commitMutationPromise } from "../../../../utils/relay";
import { logger } from "../../../../common/logger";
import { FileUploadInitMutation as FileUploadInitMutationType } from "./__generated__/FileUploadInitMutation.graphql";
import { FileUploadFinishMutation as FileUploadFinishMutationType } from "./__generated__/FileUploadFinishMutation.graphql";
import { Input } from "../../../Input";

const FileUploadInitMutation = graphql`
  mutation FileUploadInitMutation($input: InitUploadFileInput!) {
    initUploadFile(input: $input) {
      key
      uploadUrl
    }
  }
`;

const FileUploadFinishMutation = graphql`
  mutation FileUploadFinishMutation($input: FinishUploadFileInput!) {
    finishUploadFile(input: $input) {
      downloadUrl
    }
  }
`;

const FileUploadPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const [commitInit, _commitInitInFlight] =
    useMutation<FileUploadInitMutationType>(FileUploadInitMutation);
  const [commitFinish, _commitFinishInFlight] =
    useMutation<FileUploadFinishMutationType>(FileUploadFinishMutation);
  const intl = useIntl();
  const [isUploading, setIsUploading] = useState(false);

  const doUpload = async (file: File) => {
    const {
      initUploadFile: { key, uploadUrl },
    } = await commitMutationPromise(commitInit)({
      variables: {
        input: {
          filename: file.name,
          contentLength: file.size,
          contentType: file.type || "application/octet-stream",
        },
      },
    });

    const res = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Disposition": `attachment; filename="${file.name}"`,
      },
    });

    if (res.status !== 200) {
      let errorMessage;
      try {
        errorMessage = `[${res.statusText}]: ${await res.text()}`;
      } catch (e) {
        errorMessage = `[${res.statusText}]`;
      }
      throw new Error(errorMessage);
    }

    const {
      finishUploadFile: { downloadUrl },
    } = await commitMutationPromise(commitFinish)({
      variables: {
        input: {
          key,
        },
      },
    });

    return { file, downloadUrl };
  };

  const commitFiles = (images: FileList) => {
    if (!images.length) {
      return;
    }

    setIsUploading(true);

    Promise.all(Array.from(images).map(doUpload))
      .then((results) => {
        const links = results
          .map(({ file, downloadUrl }) => {
            const prefix = file.type.startsWith("image/") ? "!" : "";
            return `${prefix}[${file.name}](${downloadUrl})`;
          })
          .join(" ");
        editor.update(() => {
          const uploadedTextNode = $createTextNode(links);
          $insertNodes([uploadedTextNode]);
        });
      })
      .catch((error) => {
        logger.error(error);
        toast.error(
          intl.formatMessage({
            defaultMessage:
              "There was a error on file upload. Please try again",
          }),
        );
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  editor.registerCommand(
    DROP_COMMAND,
    (event: DragEvent, _editor: LexicalEditor) => {
      event.preventDefault();
      const files = event.dataTransfer?.files;
      if (files && files.length > 0) {
        commitFiles(files);
        return true;
      }
      return false;
    },
    1,
  );

  return (
    <Input
      className="my-4"
      type="file"
      disabled={isUploading}
      aria-disabled={isUploading}
      multiple
      onChange={(event) => {
        if (event.target.files) {
          commitFiles(event.target.files);
        }
      }}
    />
  );
};

export default FileUploadPlugin;
